import twitterText from 'twitter-text'

import {
  isValidHttpUrl,
  removeUrlProtocol,
} from '~publish/legacy/composer/composer/utils/StringUtils'

import { concatMentionsToEntities } from '../common'
import type { MentionEntity } from '../common'

const MAX_URL_LENGTH = 30

// Mirrors the lookbehind in Mastodon's Account::MENTION_RE.
const MASTODON_INVALID_MENTION_PREFIX = /[=/\p{L}\p{N}\p{M}_]$/u

const getMastodonMentions = (text: string): MentionEntity[] | null => {
  const regex =
    /@([a-zA-Z0-9](?:[a-zA-Z0-9_]*[a-zA-Z0-9])?(?:@[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?){1,2})?)/g
  const matches = [...text.matchAll(regex)].filter(
    (regexMatch) =>
      !MASTODON_INVALID_MENTION_PREFIX.test(text.slice(0, regexMatch.index)),
  )

  if (matches.length === 0) {
    return null
  }

  return matches.map((regexMatch) => {
    const match = regexMatch[0]
    const parts = match.split('@')
    const mention = parts[1]
    const displayString = `@${mention}`
    const start = regexMatch.index

    const entity: MentionEntity = {
      displayString,
      indices: [start, start + match.length],
      rawString: match,
    }

    return entity
  })
}

const getEntities = (
  text: string,
  entities: MentionEntity[],
): MentionEntity[] => {
  const mentions = getMastodonMentions(text)

  if (!mentions) {
    return entities
  }

  // Filter out mentions whose indices overlap with URL entities
  // to prevent @ symbols inside URLs from being treated as mentions
  const filteredMentions = mentions.filter((mention) => {
    return !entities.some((entity: { indices: number[] }) => {
      const [mentionStart, mentionEnd] = mention.indices
      const [entityStart, entityEnd] = entity.indices
      return mentionStart < entityEnd && entityStart < mentionEnd
    })
  })

  return concatMentionsToEntities(filteredMentions, entities)
}

const parseUrl = (url: string): string => {
  let result = removeUrlProtocol(url)
  const isUrlTooLong = result && result.length > MAX_URL_LENGTH
  if (isUrlTooLong) {
    result = `${result.substring(0, MAX_URL_LENGTH)}...`
  }

  return result
}

const parseMastodonLinks = (text: string | undefined): MentionEntity[] => {
  const entities = text
    ? twitterText
        .extractEntitiesWithIndices(text)
        .map((entity) => {
          if ('url' in entity && entity.url && isValidHttpUrl(entity.url)) {
            return {
              displayString: parseUrl(entity.url),
              indices: entity.indices,
              rawString: entity.url,
              url: entity.url,
            }
          }
          if ('hashtag' in entity && entity.hashtag) {
            return {
              displayString: `#${entity.hashtag}`,
              indices: entity.indices,
              rawString: `#${entity.hashtag}`,
              className: 'hashtag',
            }
          }
          return null
        })
        .filter((entity) => entity !== null)
    : []

  return text ? getEntities(text, entities) : []
}

export { parseMastodonLinks, getMastodonMentions }
